import { Injectable } from '@angular/core'
import { DomSanitizer, SafeUrl } from '@angular/platform-browser'
import { from, Observable } from 'rxjs'
import { map, mergeMap, switchMap, take } from 'rxjs/operators'
import * as pdfjsLib from 'pdfjs-dist'

@Injectable({
    providedIn: 'root',
})
export class ThumbnailCreationService {
    constructor(private sanitizer: DomSanitizer) {
        // PDF.js 5.x worker configuration
        pdfjsLib.GlobalWorkerOptions.workerSrc = `/assets/pdf.worker.min.mjs`

        // Alternative: Use CDN for the worker (recommended for production)
        // pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`
    }

    public getPdfThumbnail(pdfUrl: Blob): Observable<string> {
        return from(pdfUrl.arrayBuffer()).pipe(
            mergeMap((pdfData) => {
                // PDF.js 5.x uses slightly different API
                const loadingTask = pdfjsLib.getDocument({
                    data: pdfData,
                    // PDF.js 5.x enhanced options
                    useSystemFonts: true,
                    standardFontDataUrl: `/assets/standard_fonts/`,
                    verbosity: 0 // Reduce console output
                })

                return from(loadingTask.promise).pipe(
                    take(1),
                    switchMap((pdf) => from(pdf.getPage(1))),
                    switchMap((page) => {
                        const canvas = document.createElement('canvas')
                        const context = canvas.getContext('2d') as CanvasRenderingContext2D

                        // Enhanced viewport configuration for PDF.js 5.x
                        const viewport = page.getViewport({
                            scale: 2,
                            rotation: 0 // Explicit rotation parameter
                        })

                        // Maintain aspect ratio while setting max dimensions
                        const maxSize = 800
                        const scale = Math.min(
                            maxSize / viewport.width,
                            maxSize / viewport.height
                        )

                        const scaledViewport = page.getViewport({
                            scale: scale * 2, // Higher scale for better quality
                            rotation: 0
                        })

                        canvas.height = scaledViewport.height
                        canvas.width = scaledViewport.width

                        // PDF.js 5.x render task with correct options
                        const renderTask = page.render({
                            canvasContext: context,
                            canvas: canvas,
                            viewport: scaledViewport,
                            // PDF.js 5.x specific options
                            background: 'white', // Ensure white background
                            annotationMode: pdfjsLib.AnnotationMode.DISABLE
                        })

                        return from(renderTask.promise).pipe(
                            map(() => ({ canvas, page }))
                        )
                    }),
                    switchMap(({ canvas, page }) => {
                        return new Observable<string>((observer) => {
                            canvas.toBlob(
                                (blob) => {
                                    if (blob) {
                                        const reader = new FileReader()
                                        reader.onload = () => {
                                            const base64String = reader.result as string
                                            observer.next(base64String)
                                            observer.complete()
                                        }
                                        reader.onerror = () => {
                                            observer.error('Failed to convert canvas to base64')
                                        }
                                        reader.readAsDataURL(blob)
                                    } else {
                                        observer.error('Failed to create blob from canvas')
                                    }
                                },
                                'image/jpeg',
                                0.8 // Increased quality
                            )
                        })
                    })
                )
            })
        )
    }

    // Enhanced method with configurable options for PDF.js 5.x
    public getPdfThumbnailWithOptions(
        pdfUrl: Blob,
        options: {
            maxSize?: number
            quality?: number
            scale?: number
            format?: 'image/jpeg' | 'image/png'
        } = {}
    ): Observable<string> {
        const {
            maxSize = 800,
            quality = 0.8,
            scale = 2,
            format = 'image/jpeg'
        } = options

        return from(pdfUrl.arrayBuffer()).pipe(
            mergeMap((pdfData) => {
                const loadingTask = pdfjsLib.getDocument({
                    data: pdfData,
                    useSystemFonts: true,
                    standardFontDataUrl: `/assets/standard_fonts/`,
                    verbosity: 0
                })

                return from(loadingTask.promise).pipe(
                    take(1),
                    switchMap((pdf) => from(pdf.getPage(1))),
                    switchMap((page) => {
                        const canvas = document.createElement('canvas')
                        const context = canvas.getContext('2d') as CanvasRenderingContext2D

                        const viewport = page.getViewport({ scale: 1, rotation: 0 })
                        const canvasScale = Math.min(
                            maxSize / viewport.width,
                            maxSize / viewport.height
                        )

                        const scaledViewport = page.getViewport({
                            scale: canvasScale * scale,
                            rotation: 0
                        })

                        canvas.height = scaledViewport.height
                        canvas.width = scaledViewport.width

                        const renderTask = page.render({
                            canvasContext: context,
                            canvas: canvas,
                            viewport: scaledViewport,
                            background: 'white',
                            annotationMode: pdfjsLib.AnnotationMode.DISABLE
                        })

                        return from(renderTask.promise).pipe(
                            map(() => canvas)
                        )
                    }),
                    switchMap((canvas) => {
                        return new Observable<string>((observer) => {
                            canvas.toBlob(
                                (blob) => {
                                    if (blob) {
                                        const reader = new FileReader()
                                        reader.onload = () => observer.next(reader.result as string)
                                        reader.onerror = () => observer.error('Failed to read blob')
                                        reader.readAsDataURL(blob)
                                    } else {
                                        observer.error('Failed to create blob')
                                    }
                                },
                                format,
                                quality
                            )
                        })
                    })
                )
            })
        )
    }
}
